/**
 * Luxury Bespoke Single Product Configurator Controller
 */
(function() {
  'use strict';

  class HdtLuxuryConfigurator extends HTMLElement {
    constructor() {
      super();
      this.form = null;
      this.variantPicker = null;
      this.qtyInput = null;
      this.atcBtn = null;
      this.variants = [];
      this.currentVariantPrice = 0;
      this.extraPrice = 0;
      this.cardPrice = 0;
      this.giftPrice = 0;
      this.selectedDateObj = null;
    }

    connectedCallback() {
      this.formId = this.getAttribute('form');
      this.sectionId = this.getAttribute('section-id');
      
      try {
        const variantsData = this.getAttribute('data-variants');
        if (variantsData) {
          this.variants = JSON.parse(variantsData);
        }
      } catch (err) {
        console.warn('[Configurator] Could not parse variants JSON:', err);
      }

      this.init();
    }

    init() {
      // Find associated product form
      if (this.formId) {
        this.form = document.getElementById(this.formId);
      }
      if (!this.form) {
        this.form = this.closest('form') || document.querySelector(`form[id*="${this.sectionId}"]`) || document.querySelector('form[action*="/cart/add"]');
      }

      this.variantPicker = document.querySelector(`hdt-variant-picker#variant-picker-${this.sectionId}`) || document.querySelector('hdt-variant-picker');
      this.qtyInput = this.querySelector('[data-config-qty-input]');
      this.atcBtn = this.querySelector('[data-config-atc-btn]') || this.form?.querySelector('button[name="add"]');

      const initialPriceAttr = this.getAttribute('data-base-price');
      if (initialPriceAttr) {
        this.currentVariantPrice = parseFloat(initialPriceAttr);
      }

      this.initBespokeSwatches();
      this.initHatBoxSizeModal();
      this.initBespokeGifts();
      this.initSizeCards();
      this.initPresentationSwatches();
      this.initCustomOptions();
      this.initExtraProduct();
      this.initPersonalNote();
      this.initLuxuryCalendar();
      this.initQuantity();
      this.initVariantListener();
      this.initFormSubmitInterceptor();
      this.initDescriptionCleaner();
      this.matchAndUpdateVariant();
      this.updateSwatchAvailability();
      this.updatePriceDisplays();
    }

    initDescriptionCleaner() {
      // Clean up orphaned empty headings (e.g. "Care Instructions" with no content)
      const headings = document.querySelectorAll('.hdt-product-boutique-description h2, .hdt-product-boutique-description h3, .hdt-product-boutique-description h4');
      headings.forEach(h => {
        let next = h.nextElementSibling;
        let hasContent = false;
        while (next && !['H2', 'H3', 'H4'].includes(next.tagName)) {
          if (next.textContent && next.textContent.trim().length > 0) {
            hasContent = true;
            break;
          }
          next = next.nextElementSibling;
        }
        if (!hasContent) {
          h.style.display = 'none';
        }
      });
    }

    initBespokeSwatches() {
      const swatchCards = this.querySelectorAll('[data-lr-swatch-card]');

      // Initial populate for selected summaries
      const selectedCards = this.querySelectorAll('[data-lr-swatch-card].is-selected');
      selectedCards.forEach(card => {
        const optionIndex = card.getAttribute('data-option-index');
        const value = card.getAttribute('data-value');
        const displayVal = card.getAttribute('data-display-value') || (value ? value.replace(/foil/gi, 'Box') : '');
        const summaryEl = this.querySelector(`[data-lr-option-selected="${CSS.escape(optionIndex)}"]`);
        if (summaryEl && displayVal) {
          summaryEl.textContent = displayVal;
        }
      });

      swatchCards.forEach(card => {
        card.addEventListener('click', (e) => {
          e.preventDefault();
          const optionIndex = card.getAttribute('data-option-index');
          const value = card.getAttribute('data-value');
          const displayVal = card.getAttribute('data-display-value') || (value ? value.replace(/foil/gi, 'Box') : '');

          const isAlreadySelected = card.classList.contains('is-selected');
          if (isAlreadySelected && card.classList.contains('is-card-swatch')) {
            return;
          }

          const siblings = this.querySelectorAll(`[data-lr-swatch-card][data-option-index="${CSS.escape(optionIndex)}"]`);
          siblings.forEach(s => {
            s.classList.remove('is-selected');
            s.classList.remove('is-flipped-front');
            s.setAttribute('aria-checked', 'false');
          });

          card.classList.remove('is-flipped-front');
          card.classList.add('is-selected');
          card.setAttribute('aria-checked', 'true');

          const summaryEl = this.querySelector(`[data-lr-option-selected="${CSS.escape(optionIndex)}"]`);
          if (summaryEl) {
            summaryEl.textContent = displayVal;
          }

          this.matchAndUpdateVariant();
          this.updateSwatchAvailability();

          // Sync with native theme variant picker
          if (this.variantPicker) {
            const radio = this.variantPicker.querySelector(`fieldset[data-index="${CSS.escape(optionIndex)}"] input[type="radio"][value="${CSS.escape(value)}"]`) ||
                          this.variantPicker.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
            if (radio) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
      });
    }

    initHatBoxSizeModal() {
      const triggers = document.querySelectorAll('[data-hat-box-size-trigger]');
      const modal = this.querySelector('[data-hat-box-size-modal]') || document.querySelector('[data-hat-box-size-modal]');
      const overlay = this.querySelector('[data-hat-box-size-overlay]') || document.querySelector('[data-hat-box-size-overlay]');
      if (!modal || !overlay) return;

      const openModal = (e) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        overlay.style.display = 'block';
        modal.style.display = 'flex';
        document.body.classList.add('lr-modal-open');
        setTimeout(() => {
          overlay.classList.add('is-open');
          modal.classList.add('is-open');
        }, 10);
      };

      const closeModal = (e) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        overlay.classList.remove('is-open');
        modal.classList.remove('is-open');
        document.body.classList.remove('lr-modal-open');
        setTimeout(() => {
          overlay.style.display = 'none';
          modal.style.display = 'none';
        }, 250);
      };

      triggers.forEach(btn => btn.addEventListener('click', openModal));
      const closeButtons = modal.querySelectorAll('[data-hat-box-size-close]');
      closeButtons.forEach(btn => btn.addEventListener('click', closeModal));
      overlay.addEventListener('click', closeModal);

      window.toggleHatBoxSizeGuide = (open) => {
        if (open) openModal();
        else closeModal();
      };

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('is-open')) {
          closeModal();
        }
      });
    }

    updateSwatchAvailability() {
      if (!this.variants || !this.variants.length) return;

      const currentSelections = {};
      const activeSwatches = this.querySelectorAll('[data-lr-swatch-card].is-selected');
      activeSwatches.forEach(swatch => {
        const idx = parseInt(swatch.getAttribute('data-option-index'), 10);
        currentSelections[idx] = swatch.getAttribute('data-value');
      });

      const allCards = this.querySelectorAll('[data-lr-swatch-card]');
      allCards.forEach(card => {
        const optIndex = parseInt(card.getAttribute('data-option-index'), 10);
        const val = card.getAttribute('data-value');

        // Check if there is any variant with this option value that is available at all
        const hasAnyAvailable = this.variants.some(v => v.options[optIndex] === val && v.available);

        // Check if there is an available variant matching this option AND other currently selected options
        const hasComboAvailable = this.variants.some(v => {
          if (v.options[optIndex] !== val || !v.available) return false;
          for (const [otherIdxStr, otherVal] of Object.entries(currentSelections)) {
            const otherIdx = parseInt(otherIdxStr, 10);
            if (otherIdx !== optIndex && otherVal && v.options[otherIdx] !== otherVal) {
              return false;
            }
          }
          return true;
        });

        if (!hasAnyAvailable || !hasComboAvailable) {
          card.classList.add('is-sold-out');
          card.setAttribute('data-available', 'false');
        } else {
          card.classList.remove('is-sold-out');
          card.setAttribute('data-available', 'true');
        }
      });
    }

    initBespokeGifts() {
      const giftCards = this.querySelectorAll('[data-lr-gift-card]');
      const giftInput = this.querySelector('[data-lr-gift-input]');
      const giftHint = this.querySelector('[data-lr-gifts-hint]');
      const giftSummary = this.querySelector('[data-lr-gifts-summary]');

      const updateGiftSummaryAndPrice = () => {
        const selectedCards = this.querySelectorAll('[data-lr-gift-card].is-selected');
        let totalGiftPrice = 0;
        const selectedTitles = [];
        const rawTitles = [];

        selectedCards.forEach(card => {
          const price = parseFloat(card.getAttribute('data-gift-price') || card.getAttribute('data-extra-price') || '0');
          const title = (card.getAttribute('data-gift-title') || card.getAttribute('data-extra-title') || '').trim();
          totalGiftPrice += price;
          if (title) {
            rawTitles.push(title);
            selectedTitles.push(`${title} (+${price} AED)`);
          }
        });

        this.giftPrice = totalGiftPrice;
        const formattedList = selectedTitles.join(', ');

        if (giftInput) giftInput.value = formattedList;
        this.setFormProperty('properties[Add-ons]', formattedList);

        if (giftSummary) {
          if (selectedCards.length === 0) {
            giftSummary.textContent = '';
          } else if (selectedCards.length === 1) {
            giftSummary.textContent = rawTitles[0];
          } else {
            giftSummary.textContent = `${selectedCards.length} Selected (+${totalGiftPrice} AED)`;
          }
        }

        if (giftHint) {
          if (selectedCards.length === 0) {
            giftHint.textContent = '(Select multiple optional add-ons)';
          } else if (selectedCards.length === 1) {
            giftHint.textContent = `1 add-on selected (+${totalGiftPrice} AED)`;
          } else {
            giftHint.textContent = `${selectedCards.length} add-ons selected (+${totalGiftPrice} AED)`;
          }
        }

        this.updatePriceDisplays();
      };

      giftCards.forEach(card => {
        card.addEventListener('click', (e) => {
          e.preventDefault();
          const isSelected = card.classList.contains('is-selected');

          if (isSelected) {
            card.classList.remove('is-selected', 'is-active');
            card.setAttribute('aria-pressed', 'false');
          } else {
            card.classList.add('is-selected', 'is-active');
            card.setAttribute('aria-pressed', 'true');
          }

          updateGiftSummaryAndPrice();
        });

        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            card.click();
          }
        });
      });
    }

    initSizeCards() {
      const sizeCards = this.querySelectorAll('[data-size-card]');
      sizeCards.forEach(card => {
        card.addEventListener('click', (e) => {
          e.preventDefault();
          const value = card.getAttribute('data-value');
          const priceAttr = card.getAttribute('data-price');
          
          sizeCards.forEach(c => {
            c.classList.remove('is-active');
            c.setAttribute('aria-checked', 'false');
          });
          card.classList.add('is-active');
          card.setAttribute('aria-checked', 'true');

          if (priceAttr) {
            this.currentVariantPrice = parseFloat(priceAttr);
          }

          this.matchAndUpdateVariant();

          // Sync with standard theme variant picker
          if (this.variantPicker) {
            const radio = this.variantPicker.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
            if (radio) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
      });
    }

    initPresentationSwatches() {
      const swatches = this.querySelectorAll('[data-presentation-swatch]');
      swatches.forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          const value = swatch.getAttribute('data-value');
          
          swatches.forEach(s => {
            s.classList.remove('is-active');
            s.setAttribute('aria-checked', 'false');
          });
          swatch.classList.add('is-active');
          swatch.setAttribute('aria-checked', 'true');

          const metaEl = this.querySelector('[data-presentation-meta]');
          if (metaEl) {
            const vesselType = metaEl.getAttribute('data-vessel-type') || '';
            metaEl.textContent = vesselType ? `${value} • ${vesselType}` : value;
          }

          this.matchAndUpdateVariant();

          // Sync with standard theme variant picker
          if (this.variantPicker) {
            const radio = this.variantPicker.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
            if (radio) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
      });
    }

    initCustomOptions() {
      const customOptionCards = this.querySelectorAll('[data-custom-option-card]');
      customOptionCards.forEach(card => {
        card.addEventListener('click', (e) => {
          e.preventDefault();
          const optionIndex = card.getAttribute('data-option-index');
          const value = card.getAttribute('data-value');

          const siblings = this.querySelectorAll(`[data-custom-option-card][data-option-index="${CSS.escape(optionIndex)}"]`);
          siblings.forEach(s => {
            s.classList.remove('is-active');
            s.setAttribute('aria-checked', 'false');
          });

          card.classList.add('is-active');
          card.setAttribute('aria-checked', 'true');

          const summaryEl = this.querySelector(`[data-custom-option-summary="${CSS.escape(optionIndex)}"]`);
          if (summaryEl) summaryEl.textContent = value;

          this.matchAndUpdateVariant();

          if (this.variantPicker) {
            const radio = this.variantPicker.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
            if (radio) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
      });
    }

    matchAndUpdateVariant() {
      if (!this.variants || !this.variants.length) return;

      const activeSwatches = this.querySelectorAll('[data-lr-swatch-card].is-selected');
      const activeSizeCard = this.querySelector('[data-size-card].is-active');
      const activeSwatch = this.querySelector('[data-presentation-swatch].is-active');
      const activeCustomCards = this.querySelectorAll('[data-custom-option-card].is-active');

      const selectedOptions = {};
      if (activeSwatches.length > 0) {
        activeSwatches.forEach(swatch => {
          const idx = parseInt(swatch.getAttribute('data-option-index'), 10);
          selectedOptions[idx] = swatch.getAttribute('data-value');
        });
      }
      if (activeSizeCard) {
        selectedOptions[0] = activeSizeCard.getAttribute('data-value');
      }
      if (activeSwatch) {
        selectedOptions[1] = activeSwatch.getAttribute('data-value');
      }
      activeCustomCards.forEach(card => {
        const idx = parseInt(card.getAttribute('data-option-index') || '2', 10);
        selectedOptions[idx] = card.getAttribute('data-value');
      });

      // Find variant where all selected options match
      let matchedVariant = this.variants.find(v => {
        return Object.entries(selectedOptions).every(([idxStr, val]) => {
          const idx = parseInt(idxStr, 10);
          return !val || v.options[idx] === val;
        });
      });

      if (!matchedVariant) {
        matchedVariant = this.variants.find(v => {
          const sizeVal = selectedOptions[0];
          const presVal = selectedOptions[1];
          if (sizeVal && presVal) return v.options.includes(sizeVal) && v.options.includes(presVal);
          if (sizeVal) return v.options.includes(sizeVal);
          return true;
        });
      }

      if (!matchedVariant && this.variants.length > 0) {
        matchedVariant = this.variants[0];
      }

      if (matchedVariant) {
        // Update all ID inputs across forms & selects
        const idInputs = document.querySelectorAll(`
          input[name="id"], 
          select[name="id"], 
          #${CSS.escape(this.formId || '')} input[name="id"], 
          [data-main-variant-id]
        `);
        idInputs.forEach(input => {
          input.value = matchedVariant.id;
          input.disabled = false;
          input.removeAttribute('disabled');
        });

        this.currentVariantPrice = matchedVariant.price / 100.0;

        // Update button state (Available vs Sold Out)
        if (this.atcBtn) {
          const labelEl = this.atcBtn.querySelector('.hdt-luxury-atc-label') || this.atcBtn.querySelector('.hdt-luxury-atc-btn__title') || this.atcBtn.querySelector('.hdt-btn-atc_text');
          if (!matchedVariant.available) {
            this.atcBtn.disabled = true;
            this.atcBtn.classList.add('is-sold-out');
            if (labelEl) labelEl.textContent = 'SOLD OUT';
          } else {
            this.atcBtn.disabled = false;
            this.atcBtn.classList.remove('is-sold-out');
            if (labelEl) labelEl.textContent = 'ADD TO BAG';
          }
        }

        // Sync quantity input availability
        const qtyInputs = document.querySelectorAll('input[name="quantity"], [data-config-qty-input]');
        qtyInputs.forEach(qi => {
          qi.disabled = !matchedVariant.available;
        });

        // Dispatch variant change event for theme components (gallery image swap)
        document.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant: matchedVariant }
        }));

        this.updatePriceDisplays();
      }
    }

    updatePriceDisplays() {
      const activeSizeCard = this.querySelector('[data-size-card].is-active');
      let unitPrice = this.currentVariantPrice;

      if (activeSizeCard && activeSizeCard.getAttribute('data-price')) {
        unitPrice = parseFloat(activeSizeCard.getAttribute('data-price'));
      }

      const formattedPrice = `Dhs. ${unitPrice.toFixed(2)}`;

      // Update main price headers across the page
      const priceEls = document.querySelectorAll(
        '.hdt-product-main-price, .hdt-product__price .hdt-money, .hdt-product__price .hdt-price, hdt-price .hdt-money, [data-price-current]'
      );
      priceEls.forEach(el => {
        if (el.classList.contains('hdt-price-starting-from')) return;
        el.textContent = formattedPrice;
      });

      // Calculate and update overall count / total price on the ATC button
      const pageQtyInput = document.querySelector('input[name="quantity"]') || this.querySelector('[data-config-qty-input]');
      const qty = pageQtyInput ? (parseInt(pageQtyInput.value, 10) || 1) : 1;
      const giftAddon = (this.giftPrice || 0) + (this.extraPrice || 0);
      const overallTotal = (unitPrice + giftAddon + (this.cardPrice || 0)) * qty;

      const atcTotalEls = document.querySelectorAll('[data-atc-total-price]');
      atcTotalEls.forEach(el => {
        el.textContent = `AED ${Math.round(overallTotal)}`;
      });
    }

    initExtraProduct() {
      const extraCards = this.querySelectorAll('[data-extra-card]:not([data-lr-gift-card])');
      const hiddenInput = this.querySelector('[data-extra-property-input]');
      const summaryMeta = this.querySelector('[data-extra-selected-summary]');
      const shelf = this.querySelector('[data-extra-shelf]');
      const prevBtn = this.querySelector('[data-extra-prev]');
      const nextBtn = this.querySelector('[data-extra-next]');

      let isDragging = false;
      let startX = 0;
      let scrollLeft = 0;
      let dragged = false;

      // Prev / Next button slider navigation
      if (shelf) {
        prevBtn?.addEventListener('click', (e) => {
          e.preventDefault();
          shelf.scrollBy({ left: -160, behavior: 'smooth' });
        });

        nextBtn?.addEventListener('click', (e) => {
          e.preventDefault();
          shelf.scrollBy({ left: 160, behavior: 'smooth' });
        });

        // Mouse Drag to Slide
        shelf.addEventListener('mousedown', (e) => {
          isDragging = true;
          dragged = false;
          shelf.classList.add('is-dragging');
          startX = e.pageX - shelf.offsetLeft;
          scrollLeft = shelf.scrollLeft;
        });

        shelf.addEventListener('mouseleave', () => {
          isDragging = false;
          shelf.classList.remove('is-dragging');
        });

        shelf.addEventListener('mouseup', () => {
          isDragging = false;
          shelf.classList.remove('is-dragging');
        });

        shelf.addEventListener('mousemove', (e) => {
          if (!isDragging) return;
          e.preventDefault();
          const x = e.pageX - shelf.offsetLeft;
          const walk = (x - startX) * 1.5;
          if (Math.abs(walk) > 6) {
            dragged = true;
          }
          shelf.scrollLeft = scrollLeft - walk;
        });
      }

      const toggleExtraCard = (card) => {
        const title = (card.getAttribute('data-extra-title') || '').trim();
        const price = parseFloat(card.getAttribute('data-extra-price') || '0');

        // Toggle this card
        const currentlyActive = card.classList.contains('is-active');
        if (currentlyActive) {
          card.classList.remove('is-active');
          card.setAttribute('aria-pressed', 'false');
          const badge = card.querySelector('.hdt-extra-card__badge');
          if (badge) badge.textContent = '+ Add';
        } else {
          card.classList.add('is-active');
          card.setAttribute('aria-pressed', 'true');
          const badge = card.querySelector('.hdt-extra-card__badge');
          if (badge) badge.textContent = '✓ Added';
        }

        // Calculate combined total extra price
        let totalExtra = 0;
        const selectedTitles = [];

        extraCards.forEach(c => {
          if (c.classList.contains('is-active')) {
            const t = (c.getAttribute('data-extra-title') || '').trim();
            const p = parseFloat(c.getAttribute('data-extra-price') || '0');
            if (t && t.toLowerCase() !== 'none' && t.toLowerCase() !== 'no extra item' && p > 0) {
              totalExtra += p;
              selectedTitles.push(`${t} (+Dhs. ${p.toFixed(2)})`);
            }
          }
        });

        this.extraPrice = totalExtra;

        if (selectedTitles.length === 0) {
          if (hiddenInput) hiddenInput.value = '';
          if (summaryMeta) summaryMeta.textContent = 'Optional';
          this.setFormProperty('properties[Extra Add-ons]', '');
        } else {
          const propVal = selectedTitles.join(', ');
          if (hiddenInput) hiddenInput.value = propVal;
          if (summaryMeta) summaryMeta.textContent = `+Dhs. ${totalExtra.toFixed(2)}`;
          this.setFormProperty('properties[Extra Add-ons]', propVal);
        }

        this.updatePriceDisplays();
      };

      extraCards.forEach(card => {
        card.addEventListener('click', (e) => {
          if (dragged) return; // Prevent card click if user was dragging
          e.preventDefault();
          toggleExtraCard(card);
        });

        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExtraCard(card);
          }
        });
      });

      // Fallback for select if present
      const extraSelect = this.querySelector('[data-extra-select]');
      if (extraSelect) {
        extraSelect.addEventListener('change', () => {
          const selectedOption = extraSelect.options[extraSelect.selectedIndex];
          const extraPriceVal = parseFloat(selectedOption?.getAttribute('data-price') || 0);
          this.extraPrice = extraPriceVal;
          const extraTitle = selectedOption?.getAttribute('data-title') || selectedOption?.value || '';
          this.setFormProperty('properties[Extra Add-on]', extraTitle && extraTitle !== 'None' ? extraSelect.value : '');
        });
      }
    }

    initPersonalNote() {
      const cardCards = this.querySelectorAll('[data-card-item]');
      const hiddenInput = this.querySelector('[data-card-property-input]');
      const summaryMeta = this.querySelector('[data-card-selected-summary]');
      const noteWrap = this.querySelector('[data-personal-note-wrap]');
      const textarea = this.querySelector('[data-personal-note-input]');
      const charCounter = this.querySelector('[data-char-counter]');
      const floristTextarea = this.querySelector('[data-florist-instructions-input]');
      const maxChars = 250;

      // Slider navigation elements
      const prevBtn = this.querySelector('[data-card-prev]');
      const nextBtn = this.querySelector('[data-card-next]');
      const shelf = this.querySelector('[data-card-shelf]');
      const track = this.querySelector('[data-card-cards-track]');

      let isDragging = false;
      let startX = 0;
      let scrollLeft = 0;
      let dragDistance = 0;

      // Card Selection
      const selectCardOption = (card) => {
        const title = (card.getAttribute('data-card-title') || '').trim();
        const price = parseFloat(card.getAttribute('data-card-price') || '0');
        const isNone = title.toLowerCase() === 'no card' || title.toLowerCase() === 'none' || title.toLowerCase() === 'no extra item';

        cardCards.forEach(c => {
          c.classList.remove('is-active');
          c.setAttribute('aria-checked', 'false');
          const badge = c.querySelector('.hdt-extra-card__badge');
          if (badge) {
            badge.classList.remove('is-selected');
            badge.textContent = '+ Add';
          }
        });

        card.classList.add('is-active');
        card.setAttribute('aria-checked', 'true');
        const activeBadge = card.querySelector('.hdt-extra-card__badge');
        if (activeBadge) {
          activeBadge.classList.add('is-selected');
          activeBadge.textContent = '✓ Selected';
        }

        this.cardPrice = isNone ? 0 : price;

        if (isNone) {
          if (noteWrap) noteWrap.style.display = 'none';
          if (textarea) {
            textarea.value = '';
            textarea.disabled = true;
          }
          if (charCounter) charCounter.textContent = `0 / ${maxChars}`;
          if (summaryMeta) summaryMeta.textContent = 'None';
          if (hiddenInput) hiddenInput.value = '';
          this.setFormProperty('properties[Card Option]', '');
          this.setFormProperty('properties[Card Message]', '');
        } else {
          if (noteWrap) noteWrap.style.display = 'block';
          if (textarea) textarea.disabled = false;

          if (price > 0) {
            if (summaryMeta) summaryMeta.textContent = `+Dhs. ${price.toFixed(2)}`;
            const propVal = `${title} (+Dhs. ${price.toFixed(2)})`;
            if (hiddenInput) hiddenInput.value = propVal;
            this.setFormProperty('properties[Card Option]', propVal);
          } else {
            if (summaryMeta) summaryMeta.textContent = 'Complimentary';
            if (hiddenInput) hiddenInput.value = title;
            this.setFormProperty('properties[Card Option]', title);
          }
          this.setFormProperty('properties[Card Message]', textarea ? textarea.value.trim() : '');
        }

        this.updatePriceDisplays();
      };

      cardCards.forEach(card => {
        card.addEventListener('click', (e) => {
          if (dragDistance > 6) return;
          e.preventDefault();
          selectCardOption(card);
        });
      });

      // Character Counter & Recipient Message
      if (textarea) {
        textarea.addEventListener('input', () => {
          const currentLength = textarea.value.length;
          if (charCounter) charCounter.textContent = `${currentLength} / ${maxChars}`;
          this.setFormProperty('properties[Card Message]', textarea.value.trim());
        });
      }

      // Florist Instructions
      if (floristTextarea) {
        floristTextarea.addEventListener('input', () => {
          this.setFormProperty('properties[Florist Instructions]', floristTextarea.value.trim());
        });
      }

      // Slider Navigation Arrows
      const scrollCards = (direction) => {
        if (!track && !shelf) return;
        const target = track || shelf;
        const cardWidth = cardCards[0]?.offsetWidth || 150;
        const scrollAmount = (cardWidth + 12) * 1.5;
        target.scrollBy({
          left: direction === 'next' ? scrollAmount : -scrollAmount,
          behavior: 'smooth'
        });
      };

      if (prevBtn) prevBtn.addEventListener('click', (e) => { e.preventDefault(); scrollCards('prev'); });
      if (nextBtn) nextBtn.addEventListener('click', (e) => { e.preventDefault(); scrollCards('next'); });

      // Drag to scroll physics
      const scrollTarget = track || shelf;
      if (scrollTarget) {
        scrollTarget.addEventListener('mousedown', (e) => {
          isDragging = true;
          dragDistance = 0;
          startX = e.pageX - scrollTarget.offsetLeft;
          scrollLeft = scrollTarget.scrollLeft;
          scrollTarget.style.cursor = 'grabbing';
          scrollTarget.style.userSelect = 'none';
        });

        window.addEventListener('mousemove', (e) => {
          if (!isDragging) return;
          const x = e.pageX - scrollTarget.offsetLeft;
          const walk = (x - startX);
          dragDistance = Math.abs(walk);
          scrollTarget.scrollLeft = scrollLeft - walk;
        });

        window.addEventListener('mouseup', () => {
          if (!isDragging) return;
          isDragging = false;
          if (scrollTarget) {
            scrollTarget.style.cursor = '';
            scrollTarget.style.userSelect = '';
          }
        });
      }
    }

    initLuxuryCalendar() {
      const calCard = this.querySelector('[data-luxury-calendar]');
      if (!calCard) return;

      const monthSelect = calCard.querySelector('[data-cal-month]');
      const yearSelect = calCard.querySelector('[data-cal-year]');
      const prevBtn = calCard.querySelector('[data-cal-prev]');
      const nextBtn = calCard.querySelector('[data-cal-next]');
      const daysContainer = calCard.querySelector('[data-cal-days]');
      const displayNum = calCard.querySelector('[data-cal-display-num]');
      const displayMY = calCard.querySelector('[data-cal-display-my]');
      const displayDay = calCard.querySelector('[data-cal-display-day]');
      const timeSelect = calCard.querySelector('[data-cal-time-select]');
      const hiddenDateInput = this.querySelector('[data-config-delivery-date]');
      const hiddenSlotInput = this.querySelector('[data-config-delivery-slot]');

      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      const currentDay = now.getDate();
      const todayMid = new Date(currentYear, currentMonth, currentDay).getTime();
      const minSelectableTime = todayMid;

      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

      const getDubaiTimeMinutes = () => {
        try {
          const formatter = new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Asia/Dubai',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
          const parts = formatter.format(new Date()).split(':');
          let h = parseInt(parts[0], 10);
          let m = parseInt(parts[1], 10);
          if (h === 24) h = 0;
          return (isNaN(h) ? new Date().getHours() : h) * 60 + (isNaN(m) ? new Date().getMinutes() : m);
        } catch (e) {
          const d = new Date();
          const utc = d.getTime() + (d.getTimezoneOffset() * 60000);
          const dubai = new Date(utc + (3600000 * 4));
          return dubai.getHours() * 60 + dubai.getMinutes();
        }
      };

      const getDubaiHour = () => {
        return Math.floor(getDubaiTimeMinutes() / 60);
      };

      // Default selected date: Tomorrow
      const tomorrow = new Date(currentYear, currentMonth, currentDay + 1);
      this.selectedDateObj = tomorrow;

      let viewingYear = currentYear;
      let viewingMonth = currentMonth;

      if (yearSelect) {
        yearSelect.innerHTML = '';
        for (let y = currentYear; y <= currentYear + 1; y++) {
          const opt = document.createElement('option');
          opt.value = y;
          opt.textContent = y;
          if (y === viewingYear) opt.selected = true;
          yearSelect.appendChild(opt);
        }
      }

      if (monthSelect) monthSelect.value = viewingMonth;

      const updateSummaryAndProperties = () => {
        if (!this.selectedDateObj) return;

        const dNum = this.selectedDateObj.getDate();
        const mName = monthNames[this.selectedDateObj.getMonth()];
        const yNum = this.selectedDateObj.getFullYear();
        const dayName = dayNames[this.selectedDateObj.getDay()];

        if (displayNum) displayNum.textContent = dNum < 10 ? `0${dNum}` : dNum;
        if (displayMY) displayMY.textContent = `${mName} ${yNum}`;
        if (displayDay) displayDay.textContent = dayName;

        // Cutoff check for Today's order: hide/disable morning slot if Dubai hour >= 9
        const isToday = this.selectedDateObj.getDate() === currentDay &&
                        this.selectedDateObj.getMonth() === currentMonth &&
                        this.selectedDateObj.getFullYear() === currentYear;

        if (timeSelect) {
          const currentDubaiMinutes = getDubaiTimeMinutes();
          const currentHour = Math.floor(currentDubaiMinutes / 60);

          timeSelect.querySelectorAll('option[data-cutoff-hour]').forEach(opt => {
            const cutoffHour = parseInt(opt.getAttribute('data-cutoff-hour') || '9', 10);
            if (isToday && currentHour >= cutoffHour) {
              opt.hidden = true;
              opt.disabled = true;
            } else {
              opt.hidden = false;
              opt.disabled = false;
            }
          });

          // Filter Exact Time slots (data-hour): if slot start <= current Dubai time (e.g. 11:01 for 11-12), remove slot
          timeSelect.querySelectorAll('option[data-hour]').forEach(opt => {
            const slotHour = parseInt(opt.getAttribute('data-hour'), 10);
            const slotStartMin = slotHour * 60;
            if (isToday && currentDubaiMinutes >= slotStartMin) {
              opt.hidden = true;
              opt.disabled = true;
            } else {
              opt.hidden = false;
              opt.disabled = false;
            }
          });

          const currentSelected = timeSelect.querySelector(`option[value="${CSS.escape(timeSelect.value)}"]`);
          if (!currentSelected || currentSelected.disabled || currentSelected.hidden) {
            const firstAvailable = timeSelect.querySelector('option:not([disabled]):not([hidden])');
            if (firstAvailable) {
              timeSelect.value = firstAvailable.value;
            }
          }
        }

        const formattedDateStr = `${dNum} ${mName} ${yNum} (${dayName})`;
        const timeSlotVal = timeSelect ? timeSelect.value : '09:00 AM – 01:00 PM';

        if (hiddenDateInput) hiddenDateInput.value = formattedDateStr;
        if (hiddenSlotInput) hiddenSlotInput.value = timeSlotVal;

        this.setFormProperty('properties[Delivery Date]', formattedDateStr);
        this.setFormProperty('properties[Delivery Time Slot]', timeSlotVal);
      };

      const renderCalendarDays = () => {
        if (!daysContainer) return;
        daysContainer.innerHTML = '';

        const firstDayIndex = new Date(viewingYear, viewingMonth, 1).getDay();
        const lastDate = new Date(viewingYear, viewingMonth + 1, 0).getDate();

        for (let i = 0; i < firstDayIndex; i++) {
          const emptyCell = document.createElement('div');
          emptyCell.className = 'hdt-cal-day is-empty';
          emptyCell.setAttribute('aria-hidden', 'true');
          daysContainer.appendChild(emptyCell);
        }

        for (let d = 1; d <= lastDate; d++) {
          const dayBtn = document.createElement('button');
          dayBtn.type = 'button';
          dayBtn.className = 'hdt-cal-day';
          dayBtn.textContent = d;

          const cellDate = new Date(viewingYear, viewingMonth, d);
          const cellTime = cellDate.getTime();

          if (cellTime < minSelectableTime) {
            dayBtn.classList.add('is-disabled');
            dayBtn.setAttribute('disabled', 'true');
          } else {
            if (
              this.selectedDateObj &&
              this.selectedDateObj.getDate() === d &&
              this.selectedDateObj.getMonth() === viewingMonth &&
              this.selectedDateObj.getFullYear() === viewingYear
            ) {
              dayBtn.classList.add('is-selected');
              dayBtn.setAttribute('aria-selected', 'true');
            }

            dayBtn.addEventListener('click', (e) => {
              e.preventDefault();
              this.selectedDateObj = cellDate;
              daysContainer.querySelectorAll('.hdt-cal-day').forEach(el => {
                el.classList.remove('is-selected');
                el.removeAttribute('aria-selected');
              });
              dayBtn.classList.add('is-selected');
              dayBtn.setAttribute('aria-selected', 'true');
              updateSummaryAndProperties();
            });
          }

          daysContainer.appendChild(dayBtn);
        }
      };

      monthSelect?.addEventListener('change', () => {
        viewingMonth = parseInt(monthSelect.value, 10);
        renderCalendarDays();
      });

      yearSelect?.addEventListener('change', () => {
        viewingYear = parseInt(yearSelect.value, 10);
        renderCalendarDays();
      });

      prevBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        if (viewingMonth === 0) {
          if (viewingYear > currentYear) {
            viewingMonth = 11;
            viewingYear--;
          }
        } else {
          viewingMonth--;
        }
        if (monthSelect) monthSelect.value = viewingMonth;
        if (yearSelect) yearSelect.value = viewingYear;
        renderCalendarDays();
      });

      nextBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        if (viewingMonth === 11) {
          if (viewingYear < currentYear + 1) {
            viewingMonth = 0;
            viewingYear++;
          }
        } else {
          viewingMonth++;
        }
        if (monthSelect) monthSelect.value = viewingMonth;
        if (yearSelect) yearSelect.value = viewingYear;
        renderCalendarDays();
      });

      timeSelect?.addEventListener('change', () => {
        updateSummaryAndProperties();
      });

      renderCalendarDays();
      updateSummaryAndProperties();
    }

    setFormProperty(name, value) {
      if (!this.form) {
        this.form = document.getElementById(this.formId) || document.querySelector(`form[id*="${this.sectionId}"]`) || document.querySelector('form[action*="/cart/add"]');
      }
      if (!this.form) return;

      let propInput = this.form.querySelector(`input[name="${CSS.escape(name)}"]`);
      if (value && value.trim() !== '') {
        if (!propInput) {
          propInput = document.createElement('input');
          propInput.type = 'hidden';
          propInput.name = name;
          this.form.appendChild(propInput);
        }
        propInput.value = value.trim();
        propInput.disabled = false;
      } else if (propInput) {
        propInput.remove();
      }
    }

    initQuantity() {
      const allQtyInputs = document.querySelectorAll('input[name="quantity"]');
      const allMinusBtns = document.querySelectorAll('button[name="minus"], .hdt-quantity__button.is--minus');
      const allPlusBtns = document.querySelectorAll('button[name="plus"], .hdt-quantity__button.is--plus');

      const setAllQty = (val) => {
        const cleanVal = Math.max(1, parseInt(val, 10) || 1);
        allQtyInputs.forEach(inp => {
          inp.value = cleanVal;
        });
        this.updatePriceDisplays();
      };

      allMinusBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const cur = parseInt(allQtyInputs[0]?.value, 10) || 1;
          if (cur > 1) setAllQty(cur - 1);
        });
      });

      allPlusBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const cur = parseInt(allQtyInputs[0]?.value, 10) || 1;
          setAllQty(cur + 1);
        });
      });

      allQtyInputs.forEach(inp => {
        inp.addEventListener('change', () => {
          setAllQty(inp.value);
        });
      });
    }

    initFormSubmitInterceptor() {
      const resolveVariantId = async (card) => {
        let vId = card.getAttribute('data-extra-id') || card.getAttribute('data-gift-id');
        if (vId && !isNaN(parseInt(vId, 10)) && parseInt(vId, 10) > 0) {
          return parseInt(vId, 10);
        }
        const handle = card.getAttribute('data-extra-handle') || card.getAttribute('data-gift-handle');
        if (handle) {
          try {
            const res = await fetch(`/products/${handle}.js`);
            if (res.ok) {
              const prodData = await res.json();
              if (prodData.variants && prodData.variants.length > 0) {
                const matched = prodData.variants.find(v => v.available) || prodData.variants[0];
                if (matched) {
                  card.setAttribute('data-extra-id', matched.id);
                  card.setAttribute('data-gift-id', matched.id);
                  return matched.id;
                }
              }
            }
          } catch (err) {
            console.warn('[Configurator] Could not resolve variant ID for:', handle, err);
          }
        }
        return null;
      };

      const handleAddSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        this.matchAndUpdateVariant();

        const activeForm = this.form || document.getElementById(this.formId) || document.querySelector('form[action*="/cart/add"]');
        if (!activeForm) return;

        const submitBtn = this.atcBtn || activeForm.querySelector('button[type="submit"][name="add"], button[name="add"], .hdt-product-form__submit, .hdt-luxury-atc-button');

        // Hard check variant availability before proceeding
        const mainVariantIdInput = activeForm.querySelector('input[name="id"], [data-main-variant-id]');
        const mainVariantId = mainVariantIdInput ? parseInt(mainVariantIdInput.value, 10) : null;
        let currentMatchedVariant = null;
        if (mainVariantId && this.variants && this.variants.length) {
          currentMatchedVariant = this.variants.find(v => v.id === mainVariantId);
        }

        if (currentMatchedVariant && !currentMatchedVariant.available) {
          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.classList.remove('is-loading');
            submitBtn.classList.add('is-sold-out');
            const labelEl = submitBtn.querySelector('.hdt-luxury-atc-label') || submitBtn.querySelector('.hdt-luxury-atc-btn__title') || submitBtn.querySelector('.hdt-btn-atc_text');
            if (labelEl) labelEl.textContent = 'SOLD OUT';
          }
          alert('Sorry, this item is currently sold out. Please select another option.');
          return;
        }

        // Button loading state
        let originalText = '';
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.classList.add('is-loading');
          const labelEl = submitBtn.querySelector('.hdt-luxury-atc-label') || submitBtn.querySelector('.hdt-luxury-atc-btn__title') || submitBtn.querySelector('.hdt-btn-atc_text');
          if (labelEl) {
            originalText = labelEl.textContent;
            labelEl.textContent = 'ADDING...';
          }
        }

        // Construct Multi-item Cart Payload
        const items = [];
        const mainProps = {};

        // 1. Bespoke Balloon Gifts (Multi-selection)
        const activeGiftCards = this.querySelectorAll('[data-lr-gift-card].is-selected');
        const giftTitles = [];
        const giftItemsToAdd = [];

        for (const card of activeGiftCards) {
          const giftTitle = (card.getAttribute('data-gift-title') || card.getAttribute('data-extra-title') || '').trim();
          const giftPrice = parseFloat(card.getAttribute('data-gift-price') || card.getAttribute('data-extra-price') || '0');
          if (giftTitle) {
            giftTitles.push(`${giftTitle} (+${giftPrice} AED)`);
            const variantId = await resolveVariantId(card);
            if (variantId) {
              giftItemsToAdd.push({
                id: variantId,
                quantity: 1,
                properties: {
                  '_bundle_parent': 'Add-on Item',
                  '_addon': 'true'
                }
              });
            }
          }
        }

        if (giftTitles.length > 0) {
          mainProps['Add-ons'] = giftTitles.join(', ');
        }

        // 2. Extra Add-on Products (from standalone shelf if present)
        const activeExtraCards = this.querySelectorAll('[data-extra-card]:not([data-lr-gift-card]).is-active');
        const extraTitles = [];
        const extraItemsToAdd = [];

        for (const card of activeExtraCards) {
          const extraTitle = (card.getAttribute('data-extra-title') || '').trim();
          const extraPrice = parseFloat(card.getAttribute('data-extra-price') || '0');
          const isNone = !extraTitle || extraTitle.toLowerCase() === 'none' || extraTitle.toLowerCase() === 'no extra item' || extraPrice === 0;

          if (!isNone) {
            extraTitles.push(`${extraTitle} (+Dhs. ${extraPrice.toFixed(2)})`);
            const extraVarId = await resolveVariantId(card);
            if (extraVarId) {
              extraItemsToAdd.push({
                id: extraVarId,
                quantity: 1,
                properties: {
                  '_bundle_parent': 'Add-on Item',
                  '_addon': 'true'
                }
              });
            }
          }
        }

        if (extraTitles.length > 0) {
          mainProps['Extra Add-ons'] = extraTitles.join(', ');
        }

        // 3. Card Option & Message
        const activeCardOption = this.querySelector('[data-card-item].is-active');
        const cardTitle = activeCardOption ? (activeCardOption.getAttribute('data-card-title') || '').trim() : '';
        const cardPrice = activeCardOption ? parseFloat(activeCardOption.getAttribute('data-card-price') || '0') : 0;
        const cardVarId = activeCardOption ? activeCardOption.getAttribute('data-card-id') : '';
        const noteTextarea = this.querySelector('[data-personal-note-input]');
        const isNoneCard = !cardTitle || cardTitle.toLowerCase() === 'no card' || cardTitle.toLowerCase() === 'none' || cardTitle.toLowerCase() === 'no extra item';

        if (activeCardOption && !isNoneCard) {
          const cardProp = cardPrice > 0 ? `${cardTitle} (+Dhs. ${cardPrice.toFixed(2)})` : cardTitle;
          mainProps['Card Option'] = cardProp;
          const noteMsg = noteTextarea ? noteTextarea.value.trim() : '';
          if (noteMsg) {
            mainProps['Card Message'] = noteMsg;
          }
        }

        // 4. Florist Instructions
        const floristTextarea = this.querySelector('[data-florist-instructions-input]');
        if (floristTextarea && floristTextarea.value.trim()) {
          mainProps['Florist Instructions'] = floristTextarea.value.trim();
        }

        // 5. Delivery Date & Time Slot
        const dateInput = this.querySelector('[data-config-delivery-date]');
        const slotInput = this.querySelector('[data-config-delivery-slot]');
        if (dateInput && dateInput.value.trim()) {
          mainProps['Delivery Date'] = dateInput.value.trim();
        }
        if (slotInput && slotInput.value.trim()) {
          mainProps['Delivery Time Slot'] = slotInput.value.trim();
        }

        // 6. Gifting Options
        const isGiftCheckbox = this.querySelector('input[name="properties[Is a Gift]"]:checked');
        const recipientInput = this.querySelector('input[name="properties[Recipient Name]"]');
        if (isGiftCheckbox) {
          mainProps['Is a Gift'] = 'Yes';
          if (recipientInput && recipientInput.value.trim()) {
            mainProps['Recipient Name'] = recipientInput.value.trim();
          }
        }

        // 7. Main Variant & Quantity
        const finalVariantId = mainVariantId || (activeForm.querySelector('input[name="id"], [data-main-variant-id]') ? parseInt(activeForm.querySelector('input[name="id"], [data-main-variant-id]').value, 10) : null);
        const pageQtyInput = document.querySelector('input[name="quantity"]');
        const exactQty = pageQtyInput ? (parseInt(pageQtyInput.value, 10) || 1) : 1;

        if (finalVariantId) {
          items.push({
            id: finalVariantId,
            quantity: exactQty,
            properties: mainProps
          });
        }

        // 8. Add all selected balloon gift items to cart
        if (giftItemsToAdd.length > 0) {
          giftItemsToAdd.forEach(item => items.push(item));
        }

        // 9. Add all selected Extra Add-on Products to Cart
        if (extraItemsToAdd.length > 0) {
          extraItemsToAdd.forEach(item => items.push(item));
        }

        // 10. Add real Card Product to Cart if real numeric variant ID exists and price > 0
        if (!isNoneCard && cardVarId && !isNaN(parseInt(cardVarId, 10)) && parseInt(cardVarId, 10) > 0 && cardPrice > 0) {
          items.push({
            id: parseInt(cardVarId, 10),
            quantity: 1,
            properties: {
              '_bundle_parent': 'Bouquet Order',
              '_card_for': 'Bouquet Order',
              '_card_message': noteTextarea ? noteTextarea.value.trim() : ''
            }
          });
        }

        const postPayload = items.length > 0 ? JSON.stringify({ items: items }) : new FormData(activeForm);
        const postHeaders = items.length > 0
          ? { 'Content-Type': 'application/json', 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }
          : { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' };

        // Fetch add to cart with cart-drawer section
        fetch('/cart/add.js?sections=cart-drawer', {
          method: 'POST',
          body: postPayload,
          headers: postHeaders
        })
        .then(response => {
          if (!response.ok) throw new Error('Add to cart failed');
          return response.json();
        })
        .then(data => {
          // 1. Update Cart Count Badges across all header elements
          fetch('/cart.js')
            .then(r => r.json())
            .then(cart => {
              document.querySelectorAll('[data-cart-count], .la-roze-header__cart-count, #CartCount, hdt-cart-count, .hdt-count-box').forEach(badge => {
                badge.textContent = cart.item_count;
              });

              // 2. Animate header cart icons with bounce effect
              const headerCartIcons = document.querySelectorAll('.la-roze-header__icon-btn, .hdt-site-nav_cart a, #cart-icon-bubble');
              headerCartIcons.forEach(icon => {
                icon.classList.remove('hdt-cart-bounce');
                void icon.offsetWidth;
                icon.classList.add('hdt-cart-bounce');
              });
            })
            .catch(() => {});

          // 3. Show luxury animated toast notification
          showLuxuryCartToast();

          // 4. Update button to "ADDED TO BAG" success state with animation
          if (submitBtn) {
            submitBtn.classList.remove('is-loading');
            submitBtn.classList.add('is-added');
            const labelEl = submitBtn.querySelector('.hdt-luxury-atc-label') || submitBtn.querySelector('.hdt-luxury-atc-btn__title') || submitBtn.querySelector('.hdt-btn-atc_text');
            if (labelEl) {
              labelEl.innerHTML = `✓ ADDED TO BAG`;
            }
          }

          // Reset button after 2.2 seconds
          setTimeout(() => {
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.classList.remove('is-added');
              const labelEl = submitBtn.querySelector('.hdt-luxury-atc-label') || submitBtn.querySelector('.hdt-luxury-atc-btn__title') || submitBtn.querySelector('.hdt-btn-atc_text');
              if (labelEl && originalText) labelEl.textContent = originalText;
            }
          }, 2200);
        })
        .catch(err => {
          console.error('[Configurator] Add to cart error:', err);
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.classList.remove('is-loading');
            submitBtn.classList.remove('is-added');
            const labelEl = submitBtn.querySelector('.hdt-luxury-atc-label') || submitBtn.querySelector('.hdt-luxury-atc-btn__title') || submitBtn.querySelector('.hdt-btn-atc_text');
            if (labelEl && originalText) labelEl.textContent = originalText;
          }
          activeForm.submit();
        });
      };

      if (this.form) {
        this.form.addEventListener('submit', handleAddSubmit, true);
      }
      
      const allAtcButtons = document.querySelectorAll('[data-config-atc-btn], button[name="add"], .hdt-luxury-atc-button');
      allAtcButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          handleAddSubmit(e);
        }, true);
      });
    }

    initVariantListener() {
      document.addEventListener('variant:change', (e) => {
        if (e.detail && e.detail.variant) {
          this.currentVariantPrice = e.detail.variant.price / 100.0;
          this.syncActiveStates(e.detail.variant);
          this.updatePriceDisplays();
        }
      });
    }

    syncActiveStates(variant) {
      if (!variant) return;
      if (variant.options) {
        variant.options.forEach((optValue) => {
          const matchingCard = this.querySelector(`[data-size-card][data-value="${CSS.escape(optValue)}"]`);
          if (matchingCard) {
            this.querySelectorAll('[data-size-card]').forEach(c => {
              c.classList.remove('is-active');
              c.setAttribute('aria-checked', 'false');
            });
            matchingCard.classList.add('is-active');
            matchingCard.setAttribute('aria-checked', 'true');
          }

          const matchingSwatch = this.querySelector(`[data-presentation-swatch][data-value="${CSS.escape(optValue)}"]`);
          if (matchingSwatch) {
            this.querySelectorAll('[data-presentation-swatch]').forEach(s => {
              s.classList.remove('is-active');
              s.setAttribute('aria-checked', 'false');
            });
            matchingSwatch.classList.add('is-active');
            matchingSwatch.setAttribute('aria-checked', 'true');
          }

          const matchingCustomCard = this.querySelector(`[data-custom-option-card][data-value="${CSS.escape(optValue)}"]`);
          if (matchingCustomCard) {
            const optIndex = matchingCustomCard.getAttribute('data-option-index');
            this.querySelectorAll(`[data-custom-option-card][data-option-index="${CSS.escape(optIndex)}"]`).forEach(c => {
              c.classList.remove('is-active');
              c.setAttribute('aria-checked', 'false');
            });
            matchingCustomCard.classList.add('is-active');
            matchingCustomCard.setAttribute('aria-checked', 'true');
            const summaryEl = this.querySelector(`[data-custom-option-summary="${CSS.escape(optIndex)}"]`);
            if (summaryEl) summaryEl.textContent = optValue;
          }

          const matchingLrCard = this.querySelector(`[data-lr-swatch-card][data-value="${CSS.escape(optValue)}"]`);
          if (matchingLrCard) {
            const optIndex = matchingLrCard.getAttribute('data-option-index');
            this.querySelectorAll(`[data-lr-swatch-card][data-option-index="${CSS.escape(optIndex)}"]`).forEach(c => {
              c.classList.remove('is-selected');
              c.setAttribute('aria-checked', 'false');
            });
            matchingLrCard.classList.add('is-selected');
            matchingLrCard.setAttribute('aria-checked', 'true');
            const summaryEl = this.querySelector(`[data-lr-option-selected="${CSS.escape(optIndex)}"]`);
            const displayVal = matchingLrCard.getAttribute('data-display-value') || (optValue ? optValue.replace(/foil/gi, 'Box') : '');
            if (summaryEl) summaryEl.textContent = displayVal;
          }
        });
      }
    }
  }

  if (!customElements.get('hdt-luxury-configurator')) {
    customElements.define('hdt-luxury-configurator', HdtLuxuryConfigurator);
  }

  // Global Luxury Added-to-Cart Toast Helper
  window.showLuxuryCartToast = function() {
  window.location.href = '/cart';
};

  // Direct Cart Icon Click -> Cart Page (Never Drawer)
  document.addEventListener('click', (e) => {
    const cartTrigger = e.target.closest('a[href*="/cart"], #cart-icon-bubble, .la-roze-header__icon-btn[aria-label="Cart"], .hdt-site-nav_cart a');
    if (cartTrigger && !cartTrigger.closest('#CartDrawer, .hdt-mini-cart')) {
      // Direct navigation to cart page
      e.stopPropagation();
      // Allow default link behavior or redirect
      if (cartTrigger.getAttribute('href')) {
        window.location.href = cartTrigger.getAttribute('href');
      } else {
        window.location.href = '/cart';
      }
    }
  }, true);

})();
